import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrophyIcon, 
  GlobeAltIcon,
  FlagIcon,
  CameraIcon,
  HeartIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

interface LeaderboardEntry {
  uid: string;
  displayName: string;
  photoURL?: string;
  points: number;
  postcardsSent: number;
  postcardsReceived: number;
  countriesReached: number;
  rank: number;
}

export function LeaderboardPage() {
  const { currentUser, userProfile } = useAuth();
  const [globalLeaderboard, setGlobalLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [countryLeaderboard, setCountryLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<'global' | 'country' | 'friends'>('global');
  const [selectedCategory, setSelectedCategory] = useState<'points' | 'postcards' | 'countries'>('points');
  const [timeframe, setTimeframe] = useState<'all-time' | 'this-month' | 'this-week'>('all-time');

  useEffect(() => {
    fetchLeaderboards();
  }, [selectedCategory, timeframe]);

  const fetchLeaderboards = async () => {
    try {
      setLoading(true);
      
      // Determine the field to order by based on selected category
      let orderByField = 'stats.totalPoints';
      if (selectedCategory === 'postcards') {
        orderByField = 'stats.postcardsSent';
      } else if (selectedCategory === 'countries') {
        orderByField = 'stats.countriesReached';
      }

      // Fetch users ordered by the selected category
      const usersQuery = query(
        collection(db, 'users'),
        orderBy(orderByField, 'desc'),
        limit(50)
      );
      
      const querySnapshot = await getDocs(usersQuery);
      const leaderboardData: LeaderboardEntry[] = [];
      
      querySnapshot.docs.forEach((doc, index) => {
        const userData = doc.data();
        if (userData.stats && userData.displayName) {
          leaderboardData.push({
            uid: doc.id,
            displayName: userData.displayName,
            photoURL: userData.photoURL,
            points: userData.stats.totalPoints || 0,
            postcardsSent: userData.stats.postcardsSent || 0,
            postcardsReceived: userData.stats.postcardsReceived || 0,
            countriesReached: userData.stats.countriesReached || 0,
            rank: index + 1
          });
        }
      });

      // Add current user if not already in the list
      if (userProfile && !leaderboardData.find(user => user.uid === currentUser?.uid)) {
        const userEntry: LeaderboardEntry = {
          uid: currentUser?.uid || '',
          displayName: userProfile.displayName,
          photoURL: userProfile.photoURL,
          points: userProfile.stats.totalPoints,
          postcardsSent: userProfile.stats.postcardsSent,
          postcardsReceived: userProfile.stats.postcardsReceived,
          countriesReached: userProfile.stats.countriesReached,
          rank: leaderboardData.length + 1
        };
        leaderboardData.push(userEntry);
      }

      // Sort the data by the selected category
      leaderboardData.sort((a, b) => {
        if (selectedCategory === 'points') {
          return b.points - a.points;
        } else if (selectedCategory === 'postcards') {
          return b.postcardsSent - a.postcardsSent;
        } else {
          return b.countriesReached - a.countriesReached;
        }
      });

      // Update ranks after sorting
      leaderboardData.forEach((entry, index) => {
        entry.rank = index + 1;
      });

      setGlobalLeaderboard(leaderboardData);
      
      // For country leaderboard, filter by user's country (if available)
      if (userProfile?.homeCountry) {
        const countryUsers = leaderboardData.filter(user => {
          // In a real app, you'd have country information in user profiles
          // For now, we'll use a subset of the global leaderboard
          return user.uid !== currentUser?.uid; // Exclude current user from country list
        });
        setCountryLeaderboard(countryUsers.slice(0, 10));
      } else {
        setCountryLeaderboard(leaderboardData.slice(0, 10));
      }
      
    } catch (error) {
      console.error('Error fetching leaderboards:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'global', name: 'Global', icon: GlobeAltIcon },
    { id: 'country', name: 'Country', icon: FlagIcon },
    { id: 'friends', name: 'Friends', icon: HeartIcon }
  ];

  const categories = [
    { id: 'points', name: 'Total Points', icon: TrophyIcon },
    { id: 'postcards', name: 'Postcards Sent', icon: CameraIcon },
    { id: 'countries', name: 'Countries Reached', icon: GlobeAltIcon }
  ];

  const timeframes = [
    { id: 'all-time', name: 'All Time' },
    { id: 'this-month', name: 'This Month' },
    { id: 'this-week', name: 'This Week' }
  ];

  const getRankColor = (rank: number) => {
    if (rank === 1) return 'text-yellow-500';
    if (rank === 2) return 'text-gray-400';
    if (rank === 3) return 'text-amber-600';
    return 'text-gray-600';
  };

  const getRankIcon = (rank: number) => {
    if (rank <= 3) return '👑';
    if (rank <= 10) return '🏆';
    return '🎖️';
  };

  const currentLeaderboard = selectedTab === 'global' ? globalLeaderboard : countryLeaderboard;

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-yellow-600 to-orange-600 rounded-xl p-6 text-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-3xl font-bold mb-2">Leaderboards</h1>
          <p className="text-yellow-100">
            Compete with travelers worldwide
          </p>
        </motion.div>
      </div>

      {/* User's Current Rank */}
      {userProfile && (
        <div className="card bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                {userProfile.photoURL ? (
                  <img
                    src={userProfile.photoURL}
                    alt="Your avatar"
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-white font-bold text-lg">
                    {userProfile.displayName.charAt(0)}
                  </span>
                )}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Your Rank</h3>
                <p className="text-sm text-gray-600">
                  #{currentLeaderboard.findIndex(user => user.uid === currentUser?.uid) + 1 || 'Unranked'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">
                {userProfile.stats.totalPoints}
              </div>
              <div className="text-sm text-gray-600">Total Points</div>
            </div>
          </div>
        </div>
      )}

      {/* Filter Controls */}
      <div className="card space-y-4">
        {/* Tabs */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id as any)}
              className={`flex-1 flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium transition-all ${
                selectedTab === tab.id
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </div>

        {/* Category and Timeframe */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as any)}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Timeframe</label>
            <select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value as any)}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {timeframes.map((tf) => (
                <option key={tf.id} value={tf.id}>
                  {tf.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
          <TrophyIcon className="h-5 w-5 mr-2 text-yellow-600" />
          {selectedTab === 'global' ? 'Global' : selectedTab === 'country' ? 'Country' : 'Friends'} Rankings
        </h2>
        
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {currentLeaderboard.map((user, index) => (
              <motion.div
                key={user.uid}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                  user.uid === currentUser?.uid
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                } ${index < 3 ? 'bg-gradient-to-r from-yellow-50 to-orange-50' : ''}`}
              >
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">{getRankIcon(user.rank)}</span>
                    <span className={`text-xl font-bold ${getRankColor(user.rank)}`}>
                      #{user.rank}
                    </span>
                  </div>
                  
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200">
                    {user.photoURL ? (
                      <img
                        src={user.photoURL}
                        alt={user.displayName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                        {user.displayName.charAt(0)}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {user.displayName}
                      {user.uid === currentUser?.uid && (
                        <span className="ml-2 text-sm text-blue-600 font-normal">(You)</span>
                      )}
                    </h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span>📮 {user.postcardsSent}</span>
                      <span>📬 {user.postcardsReceived}</span>
                      <span>🌍 {user.countriesReached}</span>
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-2xl font-bold text-yellow-600">
                    {user.points.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">points</div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Achievements Showcase */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <StarIcon className="h-5 w-5 mr-2 text-yellow-600" />
          Recent Achievements
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-r from-yellow-100 to-orange-100 p-4 rounded-lg">
            <div className="text-2xl mb-2">🏆</div>
            <h3 className="font-semibold text-gray-900">Alex Chen</h3>
            <p className="text-sm text-gray-600">Unlocked "World Explorer"</p>
          </div>
          <div className="bg-gradient-to-r from-blue-100 to-purple-100 p-4 rounded-lg">
            <div className="text-2xl mb-2">📸</div>
            <h3 className="font-semibold text-gray-900">Maria Rodriguez</h3>
            <p className="text-sm text-gray-600">Unlocked "Photographer"</p>
          </div>
          <div className="bg-gradient-to-r from-green-100 to-blue-100 p-4 rounded-lg">
            <div className="text-2xl mb-2">🌟</div>
            <h3 className="font-semibold text-gray-900">David Kim</h3>
            <p className="text-sm text-gray-600">Unlocked "Social Butterfly"</p>
          </div>
        </div>
      </div>
    </div>
  );
} 